import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, useTheme } from '@mui/material';
import { DateTime } from 'luxon';
import { Header, Row } from '../../../components/Table/TableModels';
import ClickableLink from '../../../components/Text/ClickableLink';

import useSnackBar from '../../../hooks/useSnackBar/useSnackBar';
import useDate from '../../../hooks/useDate/useDate';
import { Sort } from '../../../models/CommonModels';
import { User, UsersListRequest } from '../../../models/UsersModels';
import { FileFormat } from '../../../models/ImportModels';

import UsersService from '../../../services/Users/UsersService';

function useESimManagerTable(activePage: number, pageSize: number, query: string, sort: Sort) {
  const [t] = useTranslation();
  const { localeDateTimeFormat } = useDate();
  const [totalUsers, setTotalUsers] = useState(0);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const snackBar = useSnackBar();
  const theme = useTheme();

  React.useEffect(() => {
    setIsLoading(true);
    const requestParams: UsersListRequest = {
      page: activePage - 1,
      pageSize,
      sort: sort.orderBy,
      sortingType: sort.order,
      filter: query,
    };
    UsersService.api.fetchUsers(requestParams).then(
      (response) => {
        setUsers(response.data.users);
        setTotalUsers(response.data.totalElements);
        setIsLoading(false);
      },
      () => {
        snackBar.showSnackBar(t('pages.eSimManager.fetchUserError'), 'error');
        setIsLoading(false);
      },
    );
  }, [activePage, pageSize, query, sort]);

  /**
   * Exports the current users list
   */
  const exportData = (format: FileFormat) => {
    setIsLoading(true);
    const requestParams: UsersListRequest = {
      page: activePage - 1,
      pageSize,
      sort: sort.orderBy,
      sortingType: sort.order.toUpperCase(),
      filter: query,
    };
    UsersService.api
      .exportData(requestParams, format, `users_report_${DateTime.now().toFormat(localeDateTimeFormat)}.${format}`)
      .catch(() => {
        snackBar.showSnackBar(t('pages.eSimManager.exportFileError'), 'error');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const headers: Header[] = useMemo(
    () => [
      {
        id: 'lastName',
        label: t('pages.eSimManager.table.headers.lastName'),
        isSortable: true,
      },

      {
        id: 'firstName',
        label: t('pages.eSimManager.table.headers.firstName'),
        isSortable: true,
      },
      {
        id: 'email',
        label: t('pages.eSimManager.table.headers.emailAddress'),
        isSortable: true,
      },
      {
        id: 'phoneNumber',
        label: t('pages.eSimManager.table.headers.phoneNumber'),
      },
      {
        id: 'devices',
        label: t('pages.eSimManager.table.headers.devices'),
      },
      {
        id: 'eSims',
        label: t('pages.eSimManager.table.headers.eSIM'),
      },
      {
        id: 'viewDetails',
        label: '',
        sx: {
          position: 'sticky',
          right: 0,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            width: 1,
            boxShadow: '-8px 0px 16px 0px #00000029',
            opacity: 'var(--sticky-actions-shadow, 0)',
            pointerEvents: 'none',
            transition: 'opacity 120ms ease',
          },
        },
      },
      /*  {
        id: 'options',
        label: '',
      }, */
    ],
    [t],
  );

  const rows: Row[] = useMemo(
    () =>
      users?.map((u, index) => ({
        id: u.id,
        cells: [
          {
            value: u.lastName,
          },
          {
            value: u.firstName,
          },
          {
            value: u.email,
          },
          {
            value: u.phoneNumber,
          },
          {
            value: u.devices,
          },
          {
            value: u.sims,
          },
          {
            value: (
              <Box
                sx={{
                  padding: '0.688rem 1rem',
                }}
              >
                <ClickableLink to={`/esimManager/userDetails/${u.id}`}>{t('common.table.viewDetails')}</ClickableLink>
              </Box>
            ),
            sx: {
              position: 'sticky',
              right: 0,
              p: 0,
              backgroundColor: index % 2 !== 0 ? theme.palette.monochrome.monoChrome2 : 'white',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                width: 1,
                boxShadow: '-8px 0px 16px 0px #00000029',
                opacity: 'var(--sticky-actions-shadow, 0)',
                pointerEvents: 'none',
                transition: 'opacity 120ms ease',
              },
            },
          },
          /* {
            value: <VerticalDotsIcon style={{ marginLeft: 'auto' }} />,
          }, */
        ],
      })),
    [t, users],
  );

  return { headers, rows, totalUsers, isLoading, exportData };
}

export default useESimManagerTable;
